// Per spec/design-system.md §Primitive 6. Two-branch template (with vs
// without `<a>` wrap when `linkUrl` present); a second two-branch decides
// whether to emit the `height` attribute (default omitted; inline style says
// `height: auto`). The `<a>` wrap composes via html`` — the inner `<img>` is
// an HtmlString that passes through the kernel's interpolation rule un-escaped
// (D-021 trust seam).
//
// **P-037.30 §item B — `border?` prop dropped.** The optional `border`
// shorthand introduced at P-037.25 D-133 (for EventCard / SponsorCard /
// Big3EventCard hairline edges) had its last consumer removed when the
// `images` Block was migrated off it. EventCard / SponsorCard /
// Big3EventCard / big-3-hero-image migrated off at P-037.29 D-137 §item 4
// (replaced by baked inset shadow on the upload bytes); the `images`
// Block followed at P-037.30 §item B. Box-sizing auto-emission and the
// border CSS branch are gone — every emit is now `border: 0;` flat.
//
// **P-037.26 D-134 — `toAbsoluteUrl` applied at emission.** The `src`
// (always) and the `linkUrl` (when wrapped in an `<a>`) flow through
// `toAbsoluteUrl` so relative paths (`/ypo-s7.png`, `/flags/al.png`,
// `/ypo-s7-email-header-*.png`) emit as absolute URLs in the rendered
// HTML. Required for View-in-Tab + Constant Contact send paths where
// the document's base origin can't resolve repo-relative paths.
// Absolute URLs (Firebase Storage chapter uploads) + CC variables pass
// through unchanged. See src/render/constants.ts for the dispatch rule.

import { html, toAbsoluteUrl } from '../render'
import { tokens } from '../tokens'
import { HtmlString } from '../types'

export type ImageAlign = 'left' | 'center' | 'right'

export interface ImageProps {
  src: string
  alt: string
  width: number
  height?: number
  align?: ImageAlign
  linkUrl?: string
  borderRadius?: number
}

export function image({
  src,
  alt,
  width,
  height,
  align = 'left',
  linkUrl,
  borderRadius = tokens.radius.none,
}: ImageProps): HtmlString {
  const absoluteSrc = toAbsoluteUrl(src)
  const heightCss = height !== undefined ? `${height}px` : 'auto'
  const style = `display: block; width: ${width}px; height: ${heightCss}; border: 0; border-radius: ${borderRadius}px; outline: none; text-decoration: none;`
  // prettier-ignore
  const img = height !== undefined
    ? html`<img src="${absoluteSrc}" alt="${alt}" width="${width}" height="${height}" align="${align}" style="${style}">`
    : html`<img src="${absoluteSrc}" alt="${alt}" width="${width}" align="${align}" style="${style}">`
  if (linkUrl === undefined) return img
  const absoluteLinkUrl = toAbsoluteUrl(linkUrl)
  // prettier-ignore
  return html`<a href="${absoluteLinkUrl}" target="_blank" rel="noopener noreferrer">${img}</a>`
}
